/**
 * One-way bridge: AutoCAD `UNDOLIMIT` sysvar writes → `editorStore`
 * `history.depthCap`.
 *
 * When the user sets UNDOLIMIT via SETVAR or the command line, the new
 * cap is written to the sysvar store (persisted to localStorage). This
 * bridge picks up the change and immediately:
 *
 *   1. Writes the new `depthCap` into `state.history`.
 *   2. Trims `past[]` to the new cap (oldest entries first) so the
 *      constraint is enforced without waiting for the next mutation.
 *   3. Increments `droppedCount` by the number of entries evicted.
 *
 * Direction is one-way (sysvar → editor state). The reverse direction
 * is not needed — `depthCap` in editorStore is not a source of truth;
 * sysvarStore owns the value.
 *
 * Call `installUndolimitSysvarBridge()` once at App boot (next to
 * `installPreviewSysvarBridge`). The returned teardown unsubscribes on
 * unmount to avoid HMR subscriber pile-up.
 */

import { useSysvarStore } from "../dynamicInput/sysvarStore";
import { useEditorStore } from "./editorStore";

export function installUndolimitSysvarBridge(): () => void {
  return useSysvarStore.subscribe((next, prev) => {
    if (next.values.UNDOLIMIT === prev.values.UNDOLIMIT) return;
    const newCap = next.values.UNDOLIMIT;
    useEditorStore.setState((s) => {
      const currentPast = s.state.history.past;
      const cappedPast =
        newCap === 0
          ? []
          : currentPast.length > newCap
            ? currentPast.slice(currentPast.length - newCap)
            : currentPast;
      const dropped = currentPast.length - cappedPast.length;
      return {
        state: {
          ...s.state,
          history: {
            ...s.state.history,
            depthCap: newCap,
            past: cappedPast,
            droppedCount: s.state.history.droppedCount + dropped,
          },
        },
      };
    });
  });
}
